import { get } from 'lodash'
import { saveAs } from 'file-saver'
import moment from 'moment'
import { formatDate } from 'src/utils/date'
import { ISSUELOG_SEVERITIES_MAP } from 'src/constants/issueLogs'

export const STATUS_MAP = {
    0: 'Pending',
    1: 'In Progress',
    4: 'Success',
    5: 'Failed',
}

export const PRIORITY_MAP = {
    0: 'Low',
    1: 'Medium',
    2: 'High',
    3: 'Urgent',
}

export const ACTION_MAP = {
    0: 'Open',
    1: 'Resolved',
    2: 'On hold',
    3: 'Ongoing',
    4: 'Evaluation',
}

export function downloadStringAsFile(
    data,
    filename,
    mimeType,
    charset = 'utf-8'
) {
    filename = filename ?? 'export.csv'

    // Attempt to auto-detect mimetype from data URL
    const hasHeaderRegex = new RegExp(`data:${mimeType}`)
    let header

    if (!data.match(hasHeaderRegex)) {
        header = `data:${mimeType}`
        if (charset) {
            header += `;charset=${charset}`
        }
    }

    const blob = new Blob([data], { type: header })
    saveAs(blob, filename)
}

/**
 * @type {{lineBreak: string, enclosure: string, sparseObjects: boolean, generateHeader: boolean, delimiter: string, columns: Object}}
 */
export const defaultOptions = {
    lineBreak: '\n',
    delimiter: ';',
    enclosure: '"',
    sparseObjects: false,
    generateHeader: true,
    columns: undefined,
}

/**
 * @param input
 * @param {{[lineBreak]: string, [delimiter]: string, [enclosure]: string, [sparseObjects]: bool, [generateHeader]: bool, columns: Object}} [options]
 * @returns {string}
 */
export function generateCsv(input, options) {
    if (input.length === 0) {
        return ''
    }
    options = Object.assign({}, defaultOptions, options)
    let columns = null
    let keys
    let result = ''
    let escapeRegex = new RegExp(`(${options.enclosure})`, 'g')

    if (options.columns) {
        keys = Object.keys(columns)
    } else if (!options.sparseObjects) {
        keys = Object.keys(input[0]).map((key) =>
            Number.isNaN(parseInt(key)) ? key : parseInt(key)
        )
    } else {
        keys = new Set()
        input.forEach((row) => {
            Object.keys(row).forEach((key) => {
                keys.add(key.replace(escapeRegex, '\\$1'))
            })
        })

        // Create an array from the set
        keys = [...keys].sort()
    }

    if (options.generateHeader) {
        result = keys.join(`${options.delimiter}`) + options.lineBreak
    }
    // Add the rows
    input.forEach(function (row) {
        keys.forEach((key) => {
            let value = get(row, key)
            if (columns) {
                value = columns[key](row)
            }
            if (value === undefined || value === null) {
                value = ''
            }
            value = value
                .toString()
                .replace(escapeRegex, '\\$1')
                .replace(/(\r\n|\r|\n)/g, ' ')

            result += `${options.enclosure}${value}${options.enclosure}`
            if (key !== keys[keys.length - 1]) {
                result += options.delimiter
            }
        })
        result += options.lineBreak
    })
    return result
}

export default async function exportCsv(data, filename, config = {}) {
    const { columns = undefined } = config

    const csvString = generateCsv(data, { columns })
    downloadStringAsFile(csvString, filename, 'text/csv')
}

export const transformRunbookData = (state) => {
    // Format filters to string
    const filtersToString = (filters) => {
        if (!filters || !filters.length) {
            return ''
        }
        return filters
            .map(
                (filter) =>
                    `${filter.label}${filter.operation}${filter.value ? filter.value : ''}`
            )
            .join('&&')
    }

    // Calculate depth columns
    const depthColumns = {}
    for (let i = 0; i < state.maxDepth + 1; i++) {
        depthColumns[i] = ''
    }

    // Get non-filtered action UIDs
    const nonFilteredActions = Object.values(state.actions.byId)
        .filter((action) => action.filteredByAction === false)
        .map((item) => item.uid)

    // Map actions to export format
    return Object.keys(state.actions.uids)
        .map((key) => state.actions.uids[key])
        .filter((uid) => nonFilteredActions.includes(uid))
        .map((uid) => {
            const depthColumnsCopy = { ...depthColumns }
            const action = state.actions.byId[uid]
            const ciDetails = state.actions.ciToUid[action.device]?.details
            depthColumnsCopy[action.depth] = 'X'

            // Handle runbook forecast times
            let activeRunbook = {}
            if (state.runbookStartTime) {
                let potentialStartTime = null
                let potentialEndTime = null
                if (action.potentialStartTime !== null) {
                    potentialStartTime = moment(state.runbookStartTime)
                        .add(action.potentialStartTime, 'minutes')
                        .toDate()
                }
                if (action.potentialEndTime !== null) {
                    potentialEndTime = moment(state.runbookStartTime)
                        .add(action.potentialEndTime, 'minutes')
                        .toDate()
                }
                activeRunbook = {
                    'Start Session': action.startTime
                        ? formatDate(action.startTime)
                        : '',
                    'End Session': action.endTime
                        ? formatDate(action.endTime)
                        : '',
                    Status: STATUS_MAP[action.status],
                    'Forecasted start time': !action.startTime
                        ? formatDate(potentialStartTime)
                        : '',
                    'Forecasted End Time':
                        (action.startTime || potentialStartTime) &&
                            !action.endTime
                            ? formatDate(potentialEndTime)
                            : '',
                }
            }

            return {
                ...depthColumnsCopy,
                Uid: action.uid,
                Customer: action.customer,
                'Owner Team': action.ownerTeam,
                'Owner User': action.ownerUser,
                'Owner Supplier': action.ownerSupplier,
                Name: action.name,
                Description: action.description,
                'Continue Branch': action.prevSibling ? 'X' : '',
                Parents: action.parents.length
                    ? `[${action.parents.join(',')}]`
                    : '',
                'Info Only': !!action.infoOnly,
                'Auto Succeed': !!action.autoSucceed,
                Reverse: !!action.reverse,
                'Estimated Duration': action.estimatedDuration || '',
                Device: action.device,
                'Custom URL': action.customUrl,
                'Availability Zones': ciDetails?.availability_zones,
                'DR Partner': ciDetails?.dr_partner,
                'DRP Method': ciDetails?.drp_method,
                Locations: ciDetails?.locations,
                'Attached To': ciDetails?.attached_to,
                'Main Availability Zones': ciDetails?.main_availability_zones,
                'Node Of Cluster': ciDetails?.node_of_cluster,
                'Guest Of Cluster': ciDetails?.guest_of_cluster,
                Filters: filtersToString(action.filters),
                'Do not start before': action.doNotStartBeforeTime,
                ...activeRunbook,
            }
        })
}
export const transformCommentLogData = (state, action) => {
    console.log('Transform Comment Log Data:', state, action)
    const results = action.data.map((item) => {
        return {
            'Runbook Id': action.runbookId,
            'Runbook name': state.name,
            'Customer info': item.action?.customer ?? '',
            Id: item.id,
            Uid: item.uid ?? '',
            'Action name': item.action?.name ?? '',
            Message: item.message ?? item.decision ?? item.issue ?? item.activity ?? '',
            'Date created': item.createdAt,
            'First name': item.createdBy.first_name,
            'Last name': item.createdBy.last_name,
            Username: item.createdBy.username,
            Email: item.createdBy.email,
        }
    })
    return results
}
export const transformIssueLogData = (state, action) => {
    const results = action.data.map((item) => {
        return {
            'Runbook Id': action.runbookId,
            'Runbook name': state.name,
            'Customer info': item.action?.customer ?? '',
            Id: item.id,
            Uid: item.actionUid ?? '',
            'Action name': item.action?.name ?? '',
            Owner: item.owner,
            Status: ACTION_MAP[item.status],
            Severity: ISSUELOG_SEVERITIES_MAP[item.severity],
            Priority: PRIORITY_MAP[item.priority],
            Description: item.description,
            Solution: item.solution,
            Due: item.due,
        }
    })
    return results
}
export const transformDecisionsLogData = (state, action) => {
    const results = action.data.map((item) => {
        return {
            'Runbook Id': action.runbookId,
            'Runbook name': state.name,
            'Session Id': item.sessionId,
            'Customer info': item.action?.customer ?? '',
            Id: item.id,
            Uid: item.actionUid ?? '',
            'Action name': item.action?.name ?? '',
            Owner: item.owner,
            Decision: item.decision,
            Reasoning: item.reasoning,
            'Created at': item.createdAt,
        }
    })
    return results
}

export const transformActivitiesLogData = (state, action) => {
    const results = action.data.map((item) => {
        return {
            'Runbook Id': action.runbookId,
            'Runbook name': state.name,
            'Session Id': item.sessionId,
            'Customer info': item.action?.customer ?? '',
            Id: item.id,
            Uid: item.actionUid ?? '',
            'Action name': item.action?.name ?? '',
            Status: ACTION_MAP[item.status],
            Priority: PRIORITY_MAP[item.priority],
            'Created at': item.createdAt,
            Due: item.due,
            Closed: item.closed,
            Owner: item.owner,
            Company: item.company,
            Source: item.source,
            Description: item.description,
            Comment: item.comments,
        }
    })
    return results
}
